import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ComplaintStatusModel } from './complaint-status.service';

export interface ComplaintTypeModel {
  id?: number;
  description: string;
}

export interface ImageModel {
  id: number;
  fileName?: string;
  imageUrl?: string;       // URL para download (downloadFileById)
  fileDownloadUri?: string; // URI retornada pelo backend após upload
  path?: string;
}

/** Espelha AddressSummaryDTO do Java */
export interface AddressSummaryModel {
  addressId?: number;
  /** Logradouro */
  streetName?: string;
  /** Número */
  number?: string;
  /** Complemento */
  complement?: string;
  /** Bairro */
  neighborhood?: string;
  /** Cidade */
  city?: string;
  /** Estado (sigla, ex: "CE") */
  state?: string;
  /** CEP */
  postalCode?: string;
  [key: string]: any;
}

/** Espelha OwnerSummaryDTO do Java (resumo do proprietário) */
export interface OwnerSummaryModel {
  ownerId?: number;
  name?: string;
  document?: string;
  /** Endereço do proprietário (AddressSummaryDTO) */
  address?: AddressSummaryModel;
  [key: string]: any;
}

/** Espelha VehicleInvolvedResponseDTO do Java */
export interface VehicleInvolvedModel {
  vehicleId?: number;
  /** Placa do veículo (plateNumber no DTO) */
  plateNumber?: string;
  placa?: string;                // alias compatível com versão anterior
  /** Município */
  municipality?: string;
  /** Estado (sigla, ex: "CE") */
  state?: string;
  /** Número do chassi (VIN) */
  vin?: string;
  /** Cor do veículo */
  color?: string;
  /** Marca/Modelo (brandModel no DTO) */
  brandModel?: string;
  marcaModelo?: string;          // alias compatível com versão anterior
  /** Tipo do veículo */
  type?: string;
  /** Espécie do veículo */
  species?: string;
  /** Categoria do veículo */
  category?: string;
  /** Ano de fabricação (Year → number no JSON) */
  yearOfManufacture?: number;
  /** Ano do modelo (Year → number no JSON) */
  yearOfModel?: number;
  /** Último ano de licenciamento (Year → number no JSON) */
  lastLicensingYear?: number;
  /** Proprietários vinculados ao veículo */
  owners?: OwnerSummaryModel[];
  [key: string]: any;
}

export interface ComplaintModel {
  id: number;
  idDenunciaApp?: string;              // String no DTO ComplaintDataDTO
  address?: string;                     // endereco no DTO
  complaintType?: ComplaintTypeModel;
  text?: string;                        // texto no DTO
  registerDate: string;                 // data_cadastro no DTO (LocalDate → ISO)
  complaintStatus?: ComplaintStatusModel;
  /** Veículos envolvidos (VehicleInvolved no DTO) */
  involvedList?: VehicleInvolvedModel[];
  imageList?: ImageModel[];
  /** Foto principal 1 (base64 ou URL, do DTO ComplaintDataDTO) */
  foto1?: string;
  /** Foto principal 2 (base64 ou URL, do DTO ComplaintDataDTO) */
  foto2?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  latitude?: string;
  longitude?: string;
}

export interface ComplaintResponseDTO {
  id: number;
  idDenunciaApp?: number;
  address?: string;
  complaintType?: {
    id?: number;
    description: string;
  };
  text?: string;
  registerDate: string;
  complaintStatus?: {
    id?: number;
    description: string;
  };
  involvedList?: any[];
  imageList?: {
    id: number;
    imageUrl: string;
  }[];
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  latitude?: string;
  longitude?: string;
}

/** Resposta do endpoint de upload (UploadFileResponseVO do Java) */
export interface UploadFileResponseVO {
  fileName: string;
  fileDownloadUri: string;
  fileType: string;
  size: number;
}

/** DTO enviado ao backend no PUT /update/{complaintId} (ComplaintRequestDTO do Java) */
export interface ComplaintRequestDTO {
  idDenunciaApp?: number;
  address?: string;
  neighborhoodId?: number;
  complaintTypeId?: number;
  text?: string;
  registerDate?: string;       // LocalDate → ISO (yyyy-MM-dd)
  complaintStatusId?: number;
  involvedVehicleIds?: number[];
  latitude?: string;
  longitude?: string;
}

// Interface auxiliar para soft delete
export interface SoftDeleteable {
  softDelete(): void;
  restore(): void;
}

@Injectable({
  providedIn: 'root'
})
export class ComplaintService {

  private readonly baseUrl = `${environment.apiUrl}/nucon/api/v1/complaint`;
  private readonly fileBaseUrl = `${environment.apiUrl}/nucon/api/v1/file`;

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) {}

  // ============================================================
  // Métodos de Complaint
  // ============================================================

  findById(id: number): Observable<ComplaintModel> {
    return this.http.get<ComplaintModel>(`${this.baseUrl}/${id}`, this.httpOptions);
  }

  findAll(): Observable<ComplaintModel[]> {
    return this.http.get<ComplaintModel[]>(`${this.baseUrl}/all`, this.httpOptions);
  }

  

  findAllByType(complaintType: ComplaintTypeModel): Observable<ComplaintModel[]> {
    const params = new HttpParams().set('complaintType', complaintType.id!);
    return this.http.get<ComplaintModel[]>(`${this.baseUrl}/all-by-type`, { ...this.httpOptions, params });
  }

  findAllByStatus(statusId: number): Observable<ComplaintModel[]> {
    const params = new HttpParams().set('statusId', statusId);
    return this.http.get<ComplaintModel[]>(`${this.baseUrl}/all-by-status`, { ...this.httpOptions, params });
  }

  /**
   * GET /all-filtered — Busca denúncias com filtros combinados.
   * Espelha o endpoint Java: getAllComplaintsFiltered(address, neighborhood, complaintType, complaintStatus)
   *
   * @param address    Logradouro (opcional)
   * @param neighborhood Bairro (opcional)
   * @param complaintType   ID ou descrição do tipo de denúncia (opcional)
   * @param complaintStatus ID ou descrição do status da denúncia (opcional)
   */
  findAllFiltered(
    address?: string,
    neighborhood?: string,
    complaintType?: number | string,
    complaintStatus?: number | string
  ): Observable<ComplaintModel[]> {
    let params = new HttpParams();
    if (address)           { params = params.set('address', address); }
    if (neighborhood)      { params = params.set('neighborhood', neighborhood); }
    if (complaintType != null)  { params = params.set('complaintType', String(complaintType)); }
    if (complaintStatus != null) { params = params.set('complaintStatus', String(complaintStatus)); }
    return this.http.get<ComplaintModel[]>(`${this.baseUrl}/all-filtered`, { ...this.httpOptions, params });
  }

  update(complaint: ComplaintModel): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/update`, { ...this.httpOptions, params: complaint as any });
  }

  deleteById(id: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/delete/${id}`, this.httpOptions);
  }


  softDeleteById(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/soft-delete/${id}`, this.httpOptions);
  }

  /**
   * PUT /update/{complaintId} — Atualiza uma denúncia com DTO.
   * Envia ComplaintRequestDTO no corpo, compatível com o backend Java.
   */
  updateComplaint(complaintId: number, dto: ComplaintRequestDTO): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/update/${complaintId}`,
      dto,
      this.httpOptions
    );
  }

  // ============================================================
  // Métodos de Arquivo (FileController)
  // ============================================================

  /**
   * Faz upload de um arquivo vinculado a uma denúncia.
   * Endpoint: POST /nucon/api/v1/file/uploadFile
   *
   * @param file Arquivo a ser enviado
   * @param complaintId ID da denúncia
   * @param origin Origem do documento (ex: 'WEB', 'APP', 'IMPORT')
   */
  uploadFile(file: File, complaintId: number, origin: string): Observable<UploadFileResponseVO> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('complaintId', complaintId.toString());
    formData.append('origin', origin);

    return this.http.post<UploadFileResponseVO>(
      `${this.fileBaseUrl}/uploadFile`,
      formData
      // Não define Content-Type — o browser define multipart/form-data com boundary
    );
  }

  /**
   * Baixa um arquivo (imagem) pelo ID da imagem.
   * Endpoint: GET /nucon/api/v1/file/downloadFileById/{imageId}
   *
   * @param imageId ID da imagem no banco
   * @returns Blob do arquivo para download
   */
  downloadFileById(imageId: number): Observable<Blob> {
    return this.http.get(`${this.fileBaseUrl}/downloadFileById/${imageId}`, {
      responseType: 'blob',
      observe: 'body'
    });
  }

  /**
   * Visualiza um arquivo (imagem) inline pelo ID da imagem.
   * Endpoint: GET /nucon/api/v1/file/view/{imageId}
   *
   * @param imageId ID da imagem no banco
   * @returns Blob da imagem para exibição inline
   */
  viewFile(imageId: number): Observable<Blob> {
    return this.http.get(`${this.fileBaseUrl}/view/${imageId}`, {
      responseType: 'blob',
      observe: 'body'
    });
  }

  /**
   * Converte um Blob em uma URL utilizável como src de <img>.
   * Lembre-se de revogar a URL com URL.revokeObjectURL() quando não for mais necessária.
   */
  blobToImageUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
  }


    /** GET /{id} — Consulta denúncia por ID */
    getComplaintById(id: string): Observable<ComplaintResponseDTO> {
      return this.http.get<ComplaintResponseDTO>(
        `${this.baseUrl}/${encodeURIComponent(id)}`,
        this.httpOptions
      );
    }
  
    /** GET /date/{date} — Consulta denúncia por data (formato esperado: yyyy-MM-dd) */
    getComplaintByDate(date: string): Observable<ComplaintResponseDTO> {
      return this.http.get<ComplaintResponseDTO>(
        `${this.baseUrl}/date/${encodeURIComponent(date)}`,
        this.httpOptions
      );
    }
}
